import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  ArrowLeft,
  Play,
  Square,
  Download,
  Share2
} from 'lucide-react';

import EvaluationLayout from '../../components/EvaluationLayout';
import Button from '../../components/ui/button';
import EvaluationTimeline from '../../components/Evaluation/EvaluationTimeline';
import EvaluationProgressTracker from '../../components/Evaluation/EvaluationProgressTracker';
import { useAuth } from '../../contexts/AuthContext';
import evaluationService from '../../services/evaluationService';

const EvaluationExecution = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [evaluation, setEvaluation] = useState(null);
  const [currentRun, setCurrentRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('progress');

  // Fetch evaluation data
  const fetchEvaluation = useCallback(async () => {
    if (!id) return;

    try {
      setLoading(true);
      const response = await evaluationService.getEvaluation(id);

      if (response.success) {
        setEvaluation(response.data);

        // Check if there's an active run
        if (response.data.active_run) {
          setCurrentRun(response.data.active_run);
          setExecuting(['queued', 'running'].includes(response.data.active_run.status));
        }
      } else {
        setError(response.error || 'Failed to load evaluation');
      }
    } catch (err) {
      console.error('Error fetching evaluation:', err);
      setError('Failed to load evaluation');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchEvaluation();
  }, [fetchEvaluation]);

  // Start evaluation execution
  const startExecution = async () => {
    if (!evaluation) return;

    try {
      setExecuting(true);
      const response = await evaluationService.executeEvaluation(evaluation.id, {
        realtime: true,
        progress_tracking: true
      });

      if (response.success) {
        setCurrentRun(response.run);
        toast.success('Evaluation execution started');
        setActiveTab('progress');
      } else {
        toast.error(response.error || 'Failed to start execution');
        setExecuting(false);
      }
    } catch (err) {
      console.error('Error starting execution:', err);
      toast.error('Failed to start execution');
      setExecuting(false);
    }
  };

  // Cancel evaluation execution
  const cancelExecution = async () => {
    if (!currentRun) return;

    try {
      const response = await evaluationService.cancelRun(currentRun.run_id);

      if (response.success) {
        toast.success('Evaluation execution cancelled');
        setExecuting(false);
        fetchEvaluation(); // Refresh data
      } else {
        toast.error(response.error || 'Failed to cancel execution');
      }
    } catch (err) {
      console.error('Error cancelling execution:', err);
      toast.error('Failed to cancel execution');
    }
  };

  // Handle execution completion
  const handleExecutionComplete = (result) => {
    setExecuting(false);
    setCurrentRun(result);
    toast.success('Evaluation completed successfully');
    setActiveTab('timeline');
  };

  // Handle execution error
  const handleExecutionError = (error) => {
    setExecuting(false);
    toast.error('Evaluation failed: ' + (error.error || 'Unknown error'));
    setActiveTab('timeline');
  };

  // Export results
  const exportResults = async () => {
    if (!currentRun) return;

    try {
      const response = await evaluationService.exportRunResults(currentRun.run_id);

      if (response.success) {
        // Trigger download
        const blob = new Blob([JSON.stringify(response.data, null, 2)], {
          type: 'application/json'
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `evaluation-${evaluation.name}-${currentRun.run_id}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        toast.success('Results exported successfully');
      } else {
        toast.error(response.error || 'Failed to export results');
      }
    } catch (err) {
      console.error('Error exporting results:', err);
      toast.error('Failed to export results');
    }
  };

  if (loading) {
    return (
      <EvaluationLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          <span className="ml-2">Loading evaluation...</span>
        </div>
      </EvaluationLayout>
    );
  }

  if (error) {
    return (
      <EvaluationLayout>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-red-800 mb-2">Error</h2>
          <p className="text-red-600 mb-4">{error}</p>
          <Button onClick={() => navigate('/evaluations')} variant="outline">
            Back to Evaluations
          </Button>
        </div>
      </EvaluationLayout>
    );
  }

  if (!evaluation) {
    return (
      <EvaluationLayout>
        <div className="text-center py-8">
          <h2 className="text-xl font-semibold mb-2">Evaluation Not Found</h2>
          <p className="text-gray-600 mb-4">The requested evaluation could not be found.</p>
          <Button onClick={() => navigate('/evaluations')} variant="outline">
            Back to Evaluations
          </Button>
        </div>
      </EvaluationLayout>
    );
  }

  return (
    <EvaluationLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button
              variant="outline"
              onClick={() => navigate('/evaluations')}
              className="flex items-center space-x-2"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Back to Evaluations</span>
            </Button>
            <div>
              <h1 className="text-2xl font-bold">{evaluation.name}</h1>
              <p className="text-gray-600">
                {evaluation.provider} • {evaluation.model} • {evaluation.type}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            {currentRun && (
              <>
                <Button
                  variant="outline"
                  onClick={exportResults}
                  className="flex items-center space-x-2"
                >
                  <Download className="h-4 w-4" />
                  <span>Export Results</span>
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    const url = window.location.href;
                    navigator.clipboard.writeText(url);
                    toast.success('Link copied to clipboard');
                  }}
                  className="flex items-center space-x-2"
                >
                  <Share2 className="h-4 w-4" />
                  <span>Share</span>
                </Button>
              </>
            )}

            {!executing && !currentRun && (
              <Button
                onClick={startExecution}
                className="flex items-center space-x-2"
              >
                <Play className="h-4 w-4" />
                <span>Start Execution</span>
              </Button>
            )}

            {executing && (
              <Button
                variant="destructive"
                onClick={cancelExecution}
                className="flex items-center space-x-2"
              >
                <Square className="h-4 w-4" />
                <span>Stop Execution</span>
              </Button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="space-y-6">
          {/* Tabs */}
          {currentRun && (
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex space-x-8">
                <button
                  onClick={() => setActiveTab('progress')}
                  className={`${activeTab === 'progress'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                >
                  Progress
                </button>
                <button
                  onClick={() => setActiveTab('timeline')}
                  className={`${activeTab === 'timeline'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                >
                  Timeline
                </button>
              </nav>
            </div>
          )}

          {/* Tab Content */}
          {activeTab === 'progress' && (
            <EvaluationProgressTracker
              runId={currentRun.run_id}
              onComplete={handleExecutionComplete}
              onError={handleExecutionError}
              showLogs={true}
            />
          )}

          {activeTab === 'timeline' && (
            <EvaluationTimeline
              runId={currentRun.run_id}
              autoRefresh={executing}
              showDetails={true}
            />
          )}
        </div>
      </div>
    </EvaluationLayout>
  );
};

export default EvaluationExecution;